import React, { ReactNode, ReactNodeArray } from 'react';
import Portal from '../Portal';
import {
  HeaderContainer,
  CloseIcon,
  ModalScroll,
  ModalWrapper,
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalCloseButton
} from './Modal.styles';

export type ModalProps = {
  /** A React child or children */
  children: ReactNode | ReactNodeArray;
  /** Controls modal open state */
  isOpen: boolean;
  /** Controls modal close */
  closeModal: (event: React.MouseEvent<HTMLDivElement>) => void;
  /** Becomes the data-cv-test id on the modal content. */
  testHook?: string;
  /** When true, clicked backdrop does not close modal */
  disableBackdropClick?: boolean;
  /** className, needed for wrapping Modal in a styled-component */
  className?: string;
  /** Modal variant options */
  variant?: 'contained' | '';
  /** Text for modal header */
  headerText?: string;
  /** A Component (usually Styled) to override the built-in component */
  Header?: typeof ModalHeader;
  /** A Component (usually Styled) to override the built-in component */
  Content?: typeof ModalContent;
  /** A Component (usually Styled) to override the built-in component */
  Backdrop?: typeof ModalBackdrop;
  /** A Component (usually Styled) to override the built-in component */
  CloseButton?: typeof ModalCloseButton;
};

const Modal = ({
  children,
  isOpen,
  closeModal,
  headerText = '',
  disableBackdropClick = false,
  className = '',
  variant = '',
  testHook = 'Modal',
  Header = ModalHeader,
  Content = ModalContent,
  Backdrop = ModalBackdrop,
  CloseButton = ModalCloseButton
}: ModalProps) => (
  <Portal isOpen={isOpen} rootClassname="modal-root">
    <ModalWrapper>
      <Backdrop
        data-cv-test={`${testHook}.Backdrop`}
        onClick={e => (disableBackdropClick ? null : closeModal(e))}
      />
      <Content
        className={className}
        data-cv-test={testHook}
        role="dialog"
        aria-modal="true"
        variant={variant}
      >
        <HeaderContainer>
          <Header>{headerText}</Header>
          <CloseButton
            onClick={closeModal}
            aria-label="Close Modal"
            data-cv-test={`${testHook}.Close`}
          >
            <CloseIcon />
          </CloseButton>
        </HeaderContainer>
        <ModalScroll>{children}</ModalScroll>
      </Content>
    </ModalWrapper>
  </Portal>
);

export default Modal;
